'use client';

import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, AvatarImage } from '@achra/ui/avatar';
import { Button } from '@achra/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@achra/ui/dropdown-menu';
import { Skeleton } from '@achra/ui/skeleton';
import { useRenownAuth } from '@powerhousedao/reactor-browser';
import { ChevronDown, LogIn, LogOut, User, UserPlus } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { CopyAnimatedIcon, CopyButton, CopyTrigger } from '@/modules/shared/components/copy-button/copy-button';
import { Identicon } from '@/modules/shared/components/identicon/identicon';
import { useMyBuilderProfile } from '@/modules/shared/hooks/use-my-builder-profile';
import * as NavbarPrimitives from '../primitives';
import { NavbarDrivesSection } from './navbar-drives-section';
interface UserButtonProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}
function AddressLabel(t0) {
  const $ = _c(8);
  const {
    address,
    displayAddress
  } = t0;
  let t1;
  if ($[0] !== displayAddress) {
    t1 = <span>{displayAddress}</span>;
    $[0] = displayAddress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CopyTrigger className="text-muted-foreground hover:text-accent-foreground"><CopyAnimatedIcon /></CopyTrigger>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== address) {
    t3 = <CopyButton value={address}>{t2}</CopyButton>;
    $[3] = address;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <DropdownMenuLabel className="flex items-center justify-between gap-2 font-normal" onSelect={_temp}>{t1}{t3}</DropdownMenuLabel>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/**
 * Desktop Sign up link that includes the current pathname as `returnTo`.
 * Wrapped in Suspense so the parent (UserButton) can be rendered as a
 * Suspense fallback during static prerender without accessing pathname.
 */
function _temp(e) {
  e.preventDefault();
}
function SignUpButton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<Button asChild={true}><Link href="/get-started">Sign up</Link></Button>}><SignUpButtonWithReturnTo /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SignUpButtonWithReturnTo() {
  const $ = _c(2);
  const pathname = usePathname();
  const t0 = `/get-started?returnTo=${encodeURIComponent(pathname)}`;
  let t1;
  if ($[0] !== t0) {
    t1 = <Button asChild={true}><Link href={t0}>Sign up</Link></Button>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Mobile Sign up option that includes the current pathname as `returnTo`.
 * Wrapped in Suspense for the same reason as SignUpButton.
 */
function SignUpOption() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<NavbarPrimitives.ActionOption asChild={true}><Link href="/get-started"><UserPlus /><span>Sign up</span></Link></NavbarPrimitives.ActionOption>}><SignUpOptionWithReturnTo /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SignUpOptionWithReturnTo() {
  const $ = _c(4);
  const pathname = usePathname();
  const t0 = `/get-started?returnTo=${encodeURIComponent(pathname)}`;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UserPlus />;
    t2 = <span>Sign up</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t0) {
    t3 = <NavbarPrimitives.ActionOption asChild={true}><Link href={t0}>{t1}{t2}</Link></NavbarPrimitives.ActionOption>;
    $[2] = t0;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Placeholder that holds the trigger's footprint (Identicon + label + chevron)
 * while we resolve the final state, so the button doesn't shift or flicker
 * through intermediate states (logged-out → Renown identity → builder profile).
 */
function UserButtonSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="bg-popover inline-flex h-9 items-center gap-2 rounded-md border px-2"><Skeleton className="size-5 shrink-0 rounded-full" /><Skeleton className="h-4 w-20" /><ChevronDown className="text-muted-foreground/40 size-4 shrink-0" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * User avatar or login button for desktop
 */
function UserButton(t0) {
  const $ = _c(45);
  const {
    open,
    onOpenChange
  } = t0;
  const auth = useRenownAuth();
  const {
    drivesQuery,
    profileQuery,
    builderProfileId
  } = useMyBuilderProfile();
  const isAuthResolving = auth.status === undefined || auth.status === "loading" || auth.status === "checking";
  const isAuthenticated = auth.status === "authorized" && Boolean(auth.address);
  const isProfileResolving = isAuthenticated && (drivesQuery.isPending || Boolean(builderProfileId) && profileQuery.isPending);
  if (isAuthResolving || isProfileResolving) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <UserButtonSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (auth.status !== "authorized" || !auth.address) {
    let t1;
    if ($[1] !== auth) {
      t1 = <Button variant="outline" onClick={() => auth.login()}>Log in</Button>;
      $[1] = auth;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SignUpButton />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1) {
      t3 = <>{t1}{t2}</>;
      $[4] = t1;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const address = auth.address;
  const displayAddress = auth.displayAddress ?? address;
  const profile = profileQuery.data;
  const displayLabel = profile?.name ?? auth.displayName ?? displayAddress;
  const avatarImage = (profile ? profile.icon : auth.avatarUrl) || undefined;
  let t1;
  if ($[6] !== avatarImage || $[7] !== displayLabel) {
    t1 = <AvatarImage src={avatarImage} alt={displayLabel} className="object-cover" />;
    $[6] = avatarImage;
    $[7] = displayLabel;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== address || $[10] !== profile) {
    t2 = profile ? <AvatarFallback className="text-[10px] font-semibold">{profile.name?.charAt(0).toUpperCase() ?? "U"}</AvatarFallback> : <AvatarFallback className="bg-transparent"><Identicon value={address} className="size-full" /></AvatarFallback>;
    $[9] = address;
    $[10] = profile;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <Avatar className="size-5">{t1}{t2}</Avatar>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== displayLabel) {
    t4 = <span>{displayLabel}</span>;
    $[15] = displayLabel;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronDown className="size-4" />;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t3 || $[19] !== t4) {
    t6 = <DropdownMenuTrigger asChild={true}><Button variant="outline" className="items-center gap-2 px-2">{t3}{t4}{t5}</Button></DropdownMenuTrigger>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== address || $[22] !== displayAddress) {
    t7 = <AddressLabel address={address} displayAddress={displayAddress} />;
    $[21] = address;
    $[22] = displayAddress;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DropdownMenuSeparator />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== auth.openProfile || $[26] !== auth.profileId) {
    t9 = auth.profileId ? <DropdownMenuItem className="cursor-pointer" onClick={auth.openProfile}><User /><span>View Profile</span></DropdownMenuItem> : null;
    $[25] = auth.openProfile;
    $[26] = auth.profileId;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuItem asChild={true} className="cursor-pointer"><Link href="/my-account"><User /><span>My Account</span></Link></DropdownMenuItem>;
    t11 = <NavbarDrivesSection />;
    $[28] = t10;
    $[29] = t11;
  } else {
    t10 = $[28];
    t11 = $[29];
  }
  let t12;
  if ($[30] !== auth) {
    t12 = () => void auth.logout();
    $[30] = auth;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <LogOut />;
    t14 = <span>Log out</span>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12) {
    t15 = <DropdownMenuItem variant="destructive" className="cursor-pointer" onClick={t12}>{t13}{t14}</DropdownMenuItem>;
    $[34] = t12;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t7 || $[38] !== t9) {
    t16 = <DropdownMenuContent align="end" className="z-170 w-56 max-md:hidden">{t7}{t8}{t9}{t10}{t11}{t15}</DropdownMenuContent>;
    $[36] = t15;
    $[37] = t7;
    $[38] = t9;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== onOpenChange || $[41] !== open || $[42] !== t16 || $[43] !== t6) {
    t17 = <DropdownMenu open={open} onOpenChange={onOpenChange}>{t6}{t16}</DropdownMenu>;
    $[40] = onOpenChange;
    $[41] = open;
    $[42] = t16;
    $[43] = t6;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}
UserButton.displayName = 'NavbarUserButton';

/**
 * User option(s) in mobile dropdown menu — returns menu items directly
 */
function UserOption() {
  const $ = _c(28);
  const auth = useRenownAuth();
  if (auth.status !== "authorized" || !auth.address) {
    let t0;
    if ($[0] !== auth) {
      t0 = () => auth.login();
      $[0] = auth;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LogIn />;
      t2 = <span>Log in</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t1 = $[2];
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t0) {
      t3 = <NavbarPrimitives.ActionOption onClick={t0}>{t1}{t2}</NavbarPrimitives.ActionOption>;
      $[4] = t0;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <SignUpOption />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t3) {
      t5 = <>{t3}{t4}</>;
      $[7] = t3;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  const address = auth.address;
  const displayAddress = auth.displayAddress ?? address;
  let t0;
  if ($[9] !== address || $[10] !== displayAddress) {
    t0 = <AddressLabel address={address} displayAddress={displayAddress} />;
    $[9] = address;
    $[10] = displayAddress;
    $[11] = t0;
  } else {
    t0 = $[11];
  }
  let t1;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <NavbarPrimitives.ActionOptionSeparator />;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== auth.openProfile || $[14] !== auth.profileId) {
    t2 = auth.profileId ? <NavbarPrimitives.ActionOption onClick={auth.openProfile}><User /><span>View Profile</span></NavbarPrimitives.ActionOption> : null;
    $[13] = auth.openProfile;
    $[14] = auth.profileId;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <NavbarPrimitives.ActionOption asChild={true}><Link href="/my-account"><User /><span>My Account</span></Link></NavbarPrimitives.ActionOption>;
    t4 = <NavbarDrivesSection ItemComponent={NavbarPrimitives.ActionOption} Separator={NavbarPrimitives.ActionOptionSeparator} />;
    $[16] = t3;
    $[17] = t4;
  } else {
    t3 = $[16];
    t4 = $[17];
  }
  let t5;
  if ($[18] !== auth) {
    t5 = () => void auth.logout();
    $[18] = auth;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LogOut />;
    t7 = <span>Log out</span>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t5) {
    t8 = <NavbarPrimitives.ActionOption variant="destructive" onClick={t5}>{t6}{t7}</NavbarPrimitives.ActionOption>;
    $[22] = t5;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t0 || $[25] !== t2 || $[26] !== t8) {
    t9 = <>{t0}{t1}{t2}{t3}{t4}{t8}</>;
    $[24] = t0;
    $[25] = t2;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
export { UserButton, UserOption };